<template>
  <section class="get-in-touch-section modern-contact-section">
    <div class="section-container">
      <!-- Enhanced Header -->
      <div class="section-header">
        <div class="header-icon-wrapper">
          <v-avatar size="120" class="header-icon modern-avatar">
            <v-icon size="60" color="primary">mdi-account-heart</v-icon>
          </v-avatar>
          <div class="icon-pulse-effect"></div>
          <div class="icon-glow-effect"></div>
        </div>
        <h2 class="section-title modern-title">{{ title }}</h2>
        <p class="section-description modern-description">{{ description }}</p>
        <div class="section-divider"></div>
      </div>

      <!-- Main Social Contact Card -->
      <div class="main-contact-wrapper">
        <v-card class="social-contact-card modern-social-card" elevation="0">
          <!-- Enhanced Social Header -->
          <div class="social-header modern-social-header">
            <h3 class="social-title modern-social-title">Let's Connect</h3>
            <p class="social-subtitle">Choose your preferred way to reach out!</p>
          </div>

          <!-- Social Media Grid -->
          <div class="social-media-grid">
            <v-hover
              v-for="(platform, index) in socialMedia"
              :key="platform.name"
              v-slot="{ hover }"
            >
              <div
                class="social-platform-card"
                :class="{ 'platform-hover': hover }"
                :style="getPlatformCardStyle(platform, hover, index)"
                @click="openLink(platform.link)"
              >
                <div class="platform-icon-wrapper">
                  <v-icon
                    :color="hover ? 'white' : platform.color"
                    size="32"
                    class="platform-icon"
                  >
                    {{ platform.icon }}
                  </v-icon>
                  <div
                    class="platform-glow"
                    :style="{ backgroundColor: platform.color + '30' }"
                    v-if="hover"
                  ></div>
                </div>

                <div class="platform-content">
                  <h4 class="platform-name">{{ platform.name }}</h4>
                  <p class="platform-description">{{ getPlatformDescription(platform.name) }}</p>
                </div>

                <!-- Enhanced Hover Effects -->
                <div
                  v-if="hover"
                  class="platform-ripple"
                  :style="{ backgroundColor: platform.color + '20' }"
                ></div>

                <div
                  class="platform-shine"
                  v-if="hover"
                ></div>
              </div>
            </v-hover>
          </div>

          <!-- Contact Statistics -->
          <div class="contact-stats modern-stats">
            <div class="stats-header">
              <h4 class="stats-title">Why Connect With Me?</h4>
            </div>
            <div class="stats-grid">
              <div class="stat-item">
                <div class="stat-icon-wrapper">
                  <v-icon color="success" size="24">mdi-clock-fast</v-icon>
                </div>
                <div class="stat-content">
                  <div class="stat-number">{{ responseTime }}</div>
                  <div class="stat-label">Response Time</div>
                </div>
              </div>

              <div class="stat-divider"></div>

              <div class="stat-item">
                <div class="stat-icon-wrapper">
                  <v-icon color="primary" size="24">mdi-heart</v-icon>
                </div>
                <div class="stat-content">
                  <div class="stat-number">{{ satisfactionRate }}</div>
                  <div class="stat-label">Client Satisfaction</div>
                </div>
              </div>

              <div class="stat-divider"></div>

              <div class="stat-item">
                <div class="stat-icon-wrapper">
                  <v-icon color="accent" size="24">mdi-rocket-launch</v-icon>
                </div>
                <div class="stat-content">
                  <div class="stat-number">100+</div>
                  <div class="stat-label">Projects Delivered</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Call to Action -->
          <div class="contact-cta" data-aos="zoom-in" data-aos-delay="1200">
            <div class="cta-content">
              <h4 class="cta-title">Ready to Start Your Project?</h4>
              <p class="cta-description">Click on any platform above and let's discuss your ideas!</p>
            </div>
            <div class="cta-badges">
              <v-chip
                v-for="badge in ctaBadges"
                :key="badge.text"
                :color="badge.color"
                text-color="white"
                class="cta-badge"
                small
              >
                <v-icon left small>{{ badge.icon }}</v-icon>
                {{ badge.text }}
              </v-chip>
            </div>
          </div>

          <!-- Card Enhancement Effects -->
          <div class="card-glow-bg"></div>
          <div class="card-gradient-overlay"></div>
        </v-card>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "GetInTouchSection",
  props: {
    title: {
      type: String,
      default: "Get In Touch"
    },
    description: {
      type: String,
      default: "Ready to start your project? Let's discuss your ideas and bring them to life!"
    },
    socialMedia: {
      type: Array,
      required: true,
      default: () => []
    },
    responseTime: {
      type: String,
      default: "< 24hrs"
    },
    satisfactionRate: {
      type: String,
      default: "98%"
    }
  },
  data() {
    return {
      socialAnimationActive: false,
      ctaBadges: [
        { text: 'Fast Response', icon: 'mdi-flash', color: 'success' },
        { text: 'Professional', icon: 'mdi-star', color: 'primary' },
        { text: 'Creative Solutions', icon: 'mdi-lightbulb', color: 'accent' },
        { text: 'Quality Work', icon: 'mdi-check-circle', color: 'info' }
      ]
    };
  },
  methods: {
    getPlatformDescription(platformName) {
      const descriptions = {
        'WhatsApp': 'Quick messages & voice calls',
        'Telegram': 'Instant messaging & file sharing',
        'Facebook': 'Social connection & updates',
        'X-Twitter': 'Quick updates & discussions',
        'LinkedIn': 'Professional networking',
        'GitHub': 'Code collaboration & projects'
      };
      return descriptions[platformName] || 'Connect with me here';
    },

    getPlatformCardStyle(platform, hover, index) {
      const baseDelay = index * 100;
      return {
        '--platform-color': platform.color,
        '--hover-bg': platform.color,
        animationDelay: `${baseDelay}ms`,
        transform: hover ? 'translateY(-12px) scale(1.03)' : 'translateY(0) scale(1)',
        background: hover
          ? `linear-gradient(135deg, ${platform.color}, ${platform.color}dd)`
          : 'rgba(255, 255, 255, 0.03)',
        borderColor: hover ? platform.color : 'rgba(255, 255, 255, 0.1)',
        boxShadow: hover
          ? `0 20px 40px ${platform.color}30, 0 0 20px ${platform.color}20, inset 0 1px 0 rgba(255,255,255,0.1)`
          : '0 8px 20px rgba(0, 0, 0, 0.1)'
      };
    },

    openLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    }
  },

  mounted() {
    // Initialize animations
    setTimeout(() => {
      this.socialAnimationActive = true;
    }, 1500);
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.get-in-touch-section {
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 20% 30%, rgba(67, 97, 238, 0.08), transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(72, 149, 239, 0.06), transparent 60%),
      radial-gradient(circle at 50% 50%, rgba(245, 101, 101, 0.03), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
}

.section-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.header-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 2rem;

  .modern-avatar {
    position: relative;
    z-index: 3;
    background: rgba(67, 97, 238, 0.1) !important;
    border: 3px solid rgba(67, 97, 238, 0.3);
    transition: all 0.4s ease;
    backdrop-filter: blur(20px);

    &:hover {
      transform: scale(1.05);
      border-color: rgba(67, 97, 238, 0.6);
    }
  }

  .icon-pulse-effect {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 160px;
    height: 160px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(67, 97, 238, 0.2), transparent 70%);
    animation: pulse 3s ease-in-out infinite;
    z-index: 1;
  }

  .icon-glow-effect {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: conic-gradient(from 0deg, transparent, rgba(67, 97, 238, 0.3), rgba(72, 149, 239, 0.3), transparent);
    animation: rotate 8s linear infinite;
    z-index: 0;
  }
}

.modern-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, $primary, $accent, #f72585);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 8px 30px rgba(67, 97, 238, 0.3);
  letter-spacing: -1px;

  .theme--light & {
    text-shadow: 0 4px 20px rgba(67, 97, 238, 0.2);
  }

  @media (max-width: $breakpoint-md) {
    font-size: 2.8rem;
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2.2rem;
  }
}

.modern-description {
  max-width: 700px;
  margin: 0 auto 2rem;
  font-size: 1.3rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;

  .theme--light & {
    color: rgba(73, 80, 87, 0.85);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1.1rem;
  }
}

.section-divider {
  width: 150px;
  height: 5px;
  background: linear-gradient(90deg, $primary, $accent, #f72585, $primary);
  background-size: 300% 100%;
  margin: 0 auto;
  border-radius: 3px;
  position: relative;
  animation: gradientFlow 4s ease-in-out infinite;

  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: linear-gradient(90deg, $primary, $accent, #f72585, $primary);
    background-size: 300% 100%;
    border-radius: 6px;
    opacity: 0.4;
    filter: blur(6px);
    animation: gradientFlow 4s ease-in-out infinite reverse;
    z-index: -1;
  }
}

.main-contact-wrapper {
  display: flex;
  justify-content: center;
}

// Modern Social Contact Card
.modern-social-card {
  width: 100%;
  max-width: 900px;
  border-radius: 28px;
  overflow: hidden;
  position: relative;
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 30px 60px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  .theme--light & {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(67, 97, 238, 0.08);
    box-shadow:
      0 30px 60px rgba(67, 97, 238, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  .card-glow-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(67, 97, 238, 0.05), transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .card-gradient-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(255,255,255,0.03), transparent);
    pointer-events: none;
    z-index: 1;
  }
}

// Enhanced Social Header
.modern-social-header {
  padding: 3rem 2rem 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.02), transparent);

  .theme--light & {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.01), transparent);
  }
}

.modern-social-title {
  font-size: 2.2rem;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.8rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.5px;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1.8rem;
  }
}

.social-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.1rem;
  font-weight: 400;
  margin-bottom: 0;

  .theme--light & {
    color: rgba(73, 80, 87, 0.75);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1rem;
  }
}

// Social Media Grid
.social-media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: $breakpoint-md) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.2rem;
    padding: 1.5rem;
  }

  @media (max-width: $breakpoint-xs) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
}

.social-platform-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.8rem;
  border-radius: 20px;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
  min-height: 100px;

  .theme--light & {
    background: rgba(255, 255, 255, 0.7) !important;
    border-color: rgba(67, 97, 238, 0.1) !important;

    &:hover {
      background: var(--hover-bg) !important;
      border-color: var(--hover-bg) !important;
    }
  }

  &.platform-hover {
    .platform-name,
    .platform-description {
      color: white !important;
    }
  }

  .platform-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    position: relative;
    flex-shrink: 0;
    transition: all 0.3s ease;

    .platform-hover & {
      background: rgba(255, 255, 255, 0.2);
    }

    .platform-icon {
      font-size: 32px;
      transition: all 0.3s ease;
    }

    .platform-glow {
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border-radius: 50%;
      filter: blur(15px);
      animation: iconGlow 1s ease-out;
      z-index: -1;
    }
  }

  .platform-content {
    flex: 1;

    .platform-name {
      font-size: 1.3rem;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 0.3rem;
      font-family: 'Poppins', sans-serif;
      transition: color 0.3s ease;

      .theme--light & {
        color: rgba(33, 37, 41, 0.9);
      }
    }

    .platform-description {
      font-size: 0.95rem;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 500;
      transition: color 0.3s ease;
      line-height: 1.4;

      .theme--light & {
        color: rgba(73, 80, 87, 0.7);
      }
    }
  }

  .platform-ripple {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    border-radius: 20px;
    animation: ripple 0.8s ease-out;
    pointer-events: none;
  }

  .platform-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    animation: shine 0.8s ease-out;
    pointer-events: none;
  }

  @media (max-width: $breakpoint-xs) {
    padding: 1.5rem;
    gap: 1.2rem;
    min-height: 80px;

    .platform-icon-wrapper {
      width: 56px;
      height: 56px;

      .platform-icon {
        font-size: 28px;
      }
    }

    .platform-content {
      .platform-name {
        font-size: 1.1rem;
      }

      .platform-description {
        font-size: 0.9rem;
      }
    }
  }
}

// Modern Statistics
.modern-stats {
  padding: 2rem;
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.02);
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  .theme--light & {
    background: rgba(67, 97, 238, 0.02);
    border-top-color: rgba(67, 97, 238, 0.08);
  }

  .stats-header {
    text-align: center;
    margin-bottom: 2rem;

    .stats-title {
      font-size: 1.6rem;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.95);
      font-family: 'Poppins', sans-serif;

      .theme--light & {
        color: rgba(33, 37, 41, 0.9);
      }
    }
  }

  .stats-grid {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2.5rem;
    flex-wrap: wrap;

    @media (max-width: $breakpoint-xs) {
      gap: 1.5rem;
    }
  }

  .stat-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    text-align: left;

    .stat-icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
      flex-shrink: 0;

      .theme--light & {
        background: rgba(67, 97, 238, 0.08);
      }
    }

    .stat-content {
      .stat-number {
        font-size: 1.6rem;
        font-weight: 800;
        color: $primary;
        line-height: 1;
        font-family: 'Poppins', sans-serif;

        @media (max-width: $breakpoint-xs) {
          font-size: 1.4rem;
        }
      }

      .stat-label {
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.7);
        font-weight: 500;
        margin-top: 0.2rem;

        .theme--light & {
          color: rgba(73, 80, 87, 0.7);
        }
      }
    }
  }

  .stat-divider {
    width: 1px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);

    .theme--light & {
      background: rgba(67, 97, 238, 0.2);
    }

    @media (max-width: $breakpoint-xs) {
      display: none;
    }
  }
}

// Call to Action Section
.contact-cta {
  padding: 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
  background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.03));
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  .theme--light & {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.02));
  }

  .cta-content {
    margin-bottom: 1.5rem;

    .cta-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.95);
      margin-bottom: 0.5rem;
      font-family: 'Poppins', sans-serif;

      .theme--light & {
        color: rgba(33, 37, 41, 0.9);
      }

      @media (max-width: $breakpoint-xs) {
        font-size: 1.3rem;
      }
    }

    .cta-description {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1rem;
      line-height: 1.6;

      .theme--light & {
        color: rgba(73, 80, 87, 0.7);
      }
    }
  }

  .cta-badges {
    display: flex;
    justify-content: center;
    gap: 0.8rem;
    flex-wrap: wrap;

    .cta-badge {
      transition: all 0.2s ease;
      font-weight: 600;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      }
    }
  }
}

// Animations
@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.8;
  }
}

@keyframes rotate {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes gradientFlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

@keyframes shine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@keyframes iconGlow {
  0% {
    transform: scale(0);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

// Responsive Design
@media (max-width: $breakpoint-md) {
  .get-in-touch-section {
    padding: 3rem 0;
  }

  .section-container {
    padding: 0 1.5rem;
  }
}

@media (max-width: $breakpoint-xs) {
  .get-in-touch-section {
    padding: 2rem 0;
  }

  .section-container {
    padding: 0 1rem;
  }

  .section-header {
    margin-bottom: 3rem;
  }

  .header-icon-wrapper {
    margin-bottom: 1.5rem;

    .modern-avatar {
      width: 100px !important;
      height: 100px !important;

      .v-icon {
        font-size: 48px !important;
      }
    }

    .icon-pulse-effect {
      width: 140px;
      height: 140px;
    }

    .icon-glow-effect {
      width: 180px;
      height: 180px;
    }
  }

  .modern-social-card {
    border-radius: 24px;
  }

  .modern-social-header {
    padding: 2rem 1.5rem 1.5rem;
  }

  .contact-cta {
    padding: 1.5rem;

    .cta-badges {
      gap: 0.5rem;
    }
  }
}
</style>